import React, { useEffect, useRef, useState } from 'react';

import {
  CartesianGrid,
  Legend,
  ReferenceLine,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Label,
} from 'recharts';

import {
  PriceYieldPoint,
  lookupAtPricePercent,
  rateAsBps,
  rateAsPercent,
} from './YieldMatrix/utils';

const AXIS_COLOR = '#868e95';
const PRICE_REFERENCE_COLOR = '#ffcf74';
const TITLE_COLOR = '#e8e9ec';
const LEGEND_TEXT_COLOR = '#e8e9ec';

const YIELD_COLOR = '#3142c4';
const SPREAD_COLOR = '#e0a028';
const DURATION_COLOR = '#0f763d';
const ABSENT = '—';
// Same name as the bid summary's row: both read the spread matrix, which is a
// constant spread over the market yield curve.
export const SPREAD_SERIES = 'Spread to Curve';

interface TooltipProps {
  active?: boolean;
  label?: string;
  zeroSpreadPrice: number;
  yieldPoints: PriceYieldPoint[];
  spreadPoints: PriceYieldPoint[];
}

export interface TooltipValues {
  hoveredPrice: number;
  yieldPct: number | null;
  spreadBps: number | null;
  duration: number;
}

// Interpolates yield + spread + duration at the cursor's exact chart-x
// (price-as-percent-of-par) instead of reading recharts' nearest-data-point
// payload. The two series have independent price grids, so reading payload
// directly makes a value jump 100+ bps — or vanish — as the cursor crosses a
// grid sample on either series. Each curve interpolates over its own domain;
// a curve absent at this price returns null (the curve genuinely "ends"
// there — out of range, or spread at/above the zero-spread price). (ENG-2749)
//
// Both the cursor label and the points are already in price-percent, so there
// is no balance arithmetic here at all — the units can't drift (ENG-2915).
export const computeTooltipValues = (
  label: string | null | undefined,
  yieldPoints: PriceYieldPoint[],
  spreadPoints: PriceYieldPoint[],
  zeroSpreadPrice: number,
): TooltipValues | null => {
  if (label == null) return null;
  const hoveredPrice = parseFloat(label);
  if (Number.isNaN(hoveredPrice)) return null;

  const yieldResult = lookupAtPricePercent(yieldPoints, hoveredPrice);
  const spreadResult = lookupAtPricePercent(spreadPoints, hoveredPrice);

  if (!yieldResult && !spreadResult) return null;

  return {
    hoveredPrice,
    yieldPct: yieldResult ? rateAsPercent(yieldResult.rate) : null,
    spreadBps:
      hoveredPrice >= zeroSpreadPrice || !spreadResult ? null : rateAsBps(spreadResult.rate),
    duration: yieldResult?.durationYears ?? spreadResult?.durationYears ?? 0,
  };
};

const CustomTooltip: React.FC<TooltipProps> = ({
  active,
  label,
  zeroSpreadPrice,
  yieldPoints,
  spreadPoints,
}) => {
  if (!active) return null;
  const values = computeTooltipValues(label, yieldPoints, spreadPoints, zeroSpreadPrice);
  if (!values) return null;
  const { hoveredPrice, yieldPct, spreadBps, duration } = values;

  // Rows render unconditionally: a curve with no value at this price shows
  // "—" instead of dropping the row, so the readout never changes shape and a
  // value never disappears/reappears as the user scrubs across the curves.
  return (
    <div
      className="font-si rounded p-4 text-xs font-bold shadow-lg"
      style={{ backgroundColor: '#e2e3e6' }}
    >
      <p className="m-0 mb-1 flex justify-between gap-4 text-gray-800" style={{ color: '#28303e' }}>
        <span>Price: </span>
        <span>{hoveredPrice.toFixed(3)}%</span>
      </p>
      <p className="m-0 mb-1 flex justify-between gap-4" style={{ color: YIELD_COLOR }}>
        <span>Yield (YTM): </span>
        <span>{yieldPct === null ? ABSENT : `${yieldPct.toFixed(3)} %`}</span>
      </p>
      <p
        className="m-0 mb-1 flex justify-between gap-4 text-gray-800"
        style={{ color: DURATION_COLOR }}
      >
        <span>Duration: </span>
        <span>{duration.toFixed(3)}</span>
      </p>
      <p className="m-0 flex justify-between gap-4 text-gray-800" style={{ color: SPREAD_COLOR }}>
        <span>{SPREAD_SERIES}: </span>
        <span>{spreadBps === null ? ABSENT : `${spreadBps.toFixed(0)} bps`}</span>
      </p>
    </div>
  );
};

interface YieldToPriceChartProps {
  // Already normalized to price-percent space by the caller's seam, so the
  // chart never touches the unpaid balance (the dollars/cents split that
  // produced ENG-2915 lives only at the normalization boundary now).
  yieldPoints: PriceYieldPoint[];
  spreadPoints: PriceYieldPoint[];
  bidPx?: string;
  width?: number;
  height?: number;
  // Receives the clicked price-percent; the caller maps it to bid basis points.
  onPointClick?: (pricePercent: number) => void;
}

interface ChartClickEvent {
  activePayload?: Array<{
    payload: { price: number };
  }>;
}

const YieldToPriceChart: React.FC<YieldToPriceChartProps> = ({
  yieldPoints,
  spreadPoints,
  width = 800,
  height = 484,
  onPointClick,
  bidPx,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width, height });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const aspectRatio = 555 / 384;
        const height = Math.min(width / aspectRatio, 500);
        setDimensions({ width, height });
      }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [width]);

  const handleChartClick = (e: ChartClickEvent) => {
    if (onPointClick && e && e.activePayload && e.activePayload.length > 0) {
      onPointClick(e.activePayload[0].payload.price);
    }
  };

  const roundToThree = (num: number) => +(Math.round(Number(num + 'e+3')) + 'e-3');

  const yieldData = yieldPoints.map((point) => ({
    price: roundToThree(point.pricePercent),
    Yield: rateAsPercent(point.rate),
    duration: point.durationYears,
  }));

  const spreadData = spreadPoints.map((point) => ({
    price: roundToThree(point.pricePercent),
    [SPREAD_SERIES]: rateAsBps(point.rate),
    duration: point.durationYears,
  }));

  // Identify the first price where the spread is effectively zero (0 bps).
  // This ensures that once the spread reaches zero, it does not increase again for any higher price.
  // The goal is to prevent misleading values where a small spread appears to increase again after dropping to zero.
  // Any price greater than this zeroSpreadPrice should display a spread of 0 bps in the tooltip.
  const zeroSpreadPrice = spreadData[0]?.price;

  const yieldValues = yieldData.map((point) => point.Yield);

  // An empty yield matrix (zero matching loans — ENG-2862) would otherwise
  // feed Math.min/max of an empty array into the Y-axis domain as ±Infinity;
  // without the yield series the chart has no primary axis to draw.
  if (yieldValues.length === 0) {
    return (
      <div
        ref={containerRef}
        data-testid="yield-to-price-chart-empty"
        className="flex h-64 items-center justify-center rounded-md border border-slate-500 bg-gray-950 text-gray-500"
      >
        No data available
      </div>
    );
  }

  const yieldMin = Math.floor(Math.min(...yieldValues));
  const yieldMax = Math.ceil(Math.max(...yieldValues));
  const yieldTicks = yieldMax - yieldMin + 1;

  return (
    <div
      ref={containerRef}
      data-testid="yield-to-price-chart"
      className="rounded-md border border-slate-500 bg-gray-950 px-[10px] py-[15px]"
    >
      <LineChart
        width={dimensions.width - 10}
        height={dimensions.height}
        margin={{ top: 0, right: 0, left: 0, bottom: 20 }}
        onClick={handleChartClick}
      >
        <CartesianGrid stroke="#3b4351" opacity="30%" />
        <XAxis
          stroke={AXIS_COLOR}
          dataKey="price"
          padding={{ left: 30, right: 30 }}
          type="number"
          domain={['dataMin', 'dataMax']}
          tickFormatter={(tick) => tick.toFixed(3)}
        >
          <Label value="PRICE" position="bottom" />
        </XAxis>
        <YAxis
          stroke={AXIS_COLOR}
          type="number"
          dataKey="Yield"
          domain={[yieldMin, yieldMax]}
          tickFormatter={(tick) => tick.toFixed(3)}
          tickCount={yieldTicks}
          padding={{ top: 10, bottom: 10 }}
        >
          <Label value="YIELD" offset={15} position="top" />
        </YAxis>
        <YAxis
          stroke={AXIS_COLOR}
          yAxisId="right"
          orientation="right"
          type="number"
          domain={[-100, 1100]}
          tickFormatter={(tick) => tick.toFixed(0)}
          tickCount={7}
        >
          <Label value="SPREAD" offset={15} position="top" dx={-10} />
        </YAxis>
        <Legend
          verticalAlign="top"
          height={60}
          align="right"
          iconType="circle"
          formatter={(value) => (
            <span style={{ color: LEGEND_TEXT_COLOR }} className="mr-2">
              {value}
            </span>
          )}
        />
        <Tooltip
          content={
            <CustomTooltip
              zeroSpreadPrice={zeroSpreadPrice || 0}
              yieldPoints={yieldPoints}
              spreadPoints={spreadPoints}
            />
          }
        />
        <Line
          type="monotone"
          data={yieldData}
          dataKey="Yield"
          stroke={YIELD_COLOR}
          dot={false}
          activeDot={false}
        />
        <Line
          type="monotone"
          data={spreadData}
          dataKey={SPREAD_SERIES}
          stroke={SPREAD_COLOR}
          dot={false}
          activeDot={false}
          yAxisId="right"
        />
        {bidPx !== null && bidPx !== undefined && (
          <ReferenceLine
            x={bidPx}
            stroke={PRICE_REFERENCE_COLOR}
            label={{
              stroke: PRICE_REFERENCE_COLOR,
              position: 'top',
              value: `${bidPx}`,
            }}
          />
        )}
        <text x={10} y={15} fill={TITLE_COLOR} style={{ fontSize: '14px' }}>
          YIELD TO PRICE
        </text>
      </LineChart>
    </div>
  );
};

export default YieldToPriceChart;
